/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *|
 *| IBM Cognos Products: Dashboard
 *|
 *| (C) Copyright IBM Corp. 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../nls/StringResources'
], (_, StringResources) => {
	/**
   	* The helper to convert autogroup spec to adopting MUI new api.
   	**/
	class AutoGroupConverter {
		constructor(options) {
			this.dataSourcesService = options.dataSourcesService;
			this.hideGroupedItems = options.hideGroupedItems || false;
			//This is used by explore where driver analysis previews will share the cached auto group columns until user decides to add the widget to the card.
			this.temporaryGroupColumnsCache = options.groupColumnIds;
			this.groupString = StringResources.get('group');
		}

		_getUniqueLabel(mdc, columnReferencesMap){

			let label = `${mdc.getLabel()} (${this.groupString})`;
			let id = mdc.getId();
			if (columnReferencesMap[id]){
				label = `${label} (${columnReferencesMap[id]})`;
				columnReferencesMap[id] ++;
			}else{
				columnReferencesMap[id] = 1;
			}
			return label;
		}

		_processCachedGroupColumns(module, id, dataItem){
			const autoGroupColumn = module.getMetadataColumn(
				this.temporaryGroupColumnsCache[id]
			);
			dataItem.binningDefinition.label  =  autoGroupColumn.getLabel();
			dataItem.binningDefinition.columnId  =  autoGroupColumn.getId();
		}

		_processGroupColumns(module, data, mdc, binningPromises, dataItem, columnReferencesMap, forceSetVisible){
			const id = mdc.getId();
			data.name = this._getUniqueLabel(mdc, columnReferencesMap);
			binningPromises.push(
				this.dataSourcesService
					.createCustomBinning(module, id, data)
					.then((customBinData) => {
						if (customBinData) {
							const autoGroupColumn = module.getMetadataColumn(
								customBinData.element.idForExpression
							);
							dataItem.binningDefinition.columnId = autoGroupColumn.getId();
							dataItem.binningDefinition.label = autoGroupColumn.getLabel();
							if (this.hideGroupedItems){
								autoGroupColumn.setHidden(true);
							}
							if (forceSetVisible){
								autoGroupColumn.setHidden(false);
							}
							if (this.temporaryGroupColumnsCache){
								this.temporaryGroupColumnsCache[id] = autoGroupColumn.getId();
							}
						}
						return customBinData;
					})
			);
		}

		/**
		*
		* @param {object} widgets - widget spec to be converted for auto-group
		* @param {string} moduleReference - optional, if exists, means module is constant
		* @param {object} groupColumnReferencesMap - optional, a map to hold auto group column versus its references number,
		*	eg. { 'column1' : 1, 'column2': 3 }, based on which we will generate auto group labels that would distinguish
		*	from each other, eg. column1 (Group) (1), column2 (Group) (3)
		* @returns Promise with widgets spec
		*/
		convert(widgets, moduleReference, groupColumnReferencesMap, forceSetVisible = false ) {
			const promises = [];
			const binningPromises = [];
			const columnReferencesMap = groupColumnReferencesMap || {};

			_.each(widgets, (widget) => {
				const dataViews = widget.data && widget.data.dataViews;
				const modelRef = moduleReference ? moduleReference : dataViews && dataViews[0] && dataViews[0].modelRef;
				if (modelRef && dataViews) {
					// get data module with dataset service
					const promise = this._getModule(modelRef).then((module) => {
						_.each(dataViews, (dataView) => {
							let dataItems = dataView.dataItems;
							if (dataItems) {
								_.each(dataItems, (dataItem) => {
									const binningDefinition = dataItem.binningDefinition;
									if (binningDefinition) {
										let data = {};
										if (binningDefinition.binning) {
											const bins = binningDefinition.binning.auto.numberOfBins;
											data = {
												numberOfBins: bins.toString()
											};
										}
										const mdc = module.getMetadataColumn(dataItem.itemId);
										if (mdc) {
											const id = mdc.getId();
											if (this.temporaryGroupColumnsCache && this.temporaryGroupColumnsCache[id]){
												this._processCachedGroupColumns(module, id, dataItem);
											}else{
												this._processGroupColumns(module, data, mdc,  binningPromises,dataItem, columnReferencesMap, forceSetVisible);
											}

										}
									}
								});
							}
						});
					});
					promises.push(promise);
				}
			});
			return Promise.all(promises).then(() => {
				return Promise.all(binningPromises).then(() => {
					return widgets;
				});
			});
		}

		/**
		* Scan the widget spec and remove grouped columns from temporary cache so that they will be re-generated next time
		* This is used by explore when a new widget is added from driver analysis previews where auto group columns are shared
		* @param {object} widgets - widget spec to be scanned
		* @param {string} moduleReference - module reference id
		* @returns Promise
		*/
		removeTemporaryGroupColumnsForWidgets(widgets, moduleReference) {
			const promises = [];
			_.each(widgets, (widget) => {
				const dataViews = widget.data && widget.data.dataViews;
				if (dataViews) {
					const modelRef = moduleReference ? moduleReference : dataViews && dataViews[0] && dataViews[0].modelRef;
					if (modelRef){
						promises.push(this._getModule(modelRef).then((module) => {
							_.each(dataViews, (dataView) => {
								if (dataView.dataItems) {
									_.each(dataView.dataItems, (dataItem) => {
										if (dataItem.binningDefinition) {
											const column = module.getMetadataColumn(dataItem.itemId);
											if (column) {
												const id = column.getId();
												if (this.temporaryGroupColumnsCache && this.temporaryGroupColumnsCache[id]){
													delete this.temporaryGroupColumnsCache[id];
												}
											}
										}
									});
								}
							});
						}));
					}
				}
			});
			return Promise.all(promises);
		}

		/**
		* remove temporary grouped columns from metadata tree
		* @param {string} moduleReference - module reference id
		* @returns Promise
		*/
		removeTemporaryGroupColumnsForModel(modelRef){
			if (this.temporaryGroupColumnsCache && modelRef){
				return this._getModule(modelRef).then((module) => {
					const promises = [];
					_.each(this.temporaryGroupColumnsCache, (columnId) => {
						promises.push(this.dataSourcesService
							.removeCustomBinning(module, columnId));
					});
					return Promise.all(promises).then(() => {
						delete this.temporaryGroupColumnsCache;
					});
				});

			}else{
				return Promise.resolve();
			}
		}

		_getModule(modelRef) {
			return this.dataSourcesService.getModule(modelRef).then((module) => {
				return module.ensureTemporaryModule({ forceTemporaryModule: true }).then(() => module);
			});
		}
	}

	return AutoGroupConverter;
});
